import React from 'react';
import dayjs from 'dayjs';
import { clone, isNil, sortBy } from 'lodash';
import { Button, ButtonProps, Tag, Tooltip, theme } from 'antd';
import { GlobalToken } from 'antd/lib/theme';
import CopyItem from './CopyItem';
import OverflowTooltip from './OverflowTooltip';

interface CellProps {
  title: string | JSX.Element;
  id?: string | number;
  subTitle?: string | JSX.Element;
  brackets?: boolean;
  token?: GlobalToken;
}

function Cell({
  title,
  id,
  subTitle = !isNil(id) ? String(id) : id,
  brackets = true,
  token,
}: CellProps) {
  if (!subTitle) return <>{title}</>;

  subTitle = (
    <>
      {brackets ? '(' : null}
      {subTitle}
      {brackets ? ')' : null}
    </>
  );

  return (
    <>
      <span className="row-title">{title}</span>
      <br />
      {id ? (
        <CopyItem text={id} className="row-sub-title" style={{ color: token?.colorTextSecondary }}>
          {subTitle}
        </CopyItem>
      ) : (
        <div className="row-sub-title" style={{ color: token?.colorTextSecondary }}>
          {subTitle}
        </div>
      )}
    </>
  );
}

Cell.DateTime = (val: string) => {
  if (!val) return null;
  const time = dayjs(val);
  return <Cell title={time.format('YYYY-MM-DD')} subTitle={time.format('HH:mm:ss')} />;
};

Cell.Date = (val: string) => {
  if (!val) return null;
  const time = dayjs(val);
  return <Cell title={time.format('YYYY-MM-DD')} />;
};

interface CellLinkProps {
  title: string;
  id?: CellProps['id'];
  onClick: ButtonProps['onClick'];
  inline?: boolean;
  titleCopy?: boolean;
}

Cell.Link = ({ title, id, onClick, inline, titleCopy }: CellLinkProps) => {
  const { token } = theme.useToken();

  let titleEl = (
    <Button
      type="link"
      onClick={onClick}
      style={
        titleCopy ? { width: 'auto', color: token.colorPrimary } : { color: token.colorPrimary }
      }
    >
      {OverflowTooltip(title)}
    </Button>
  );
  if (titleCopy) titleEl = <CopyItem text={title}>{titleEl}</CopyItem>;
  const children = <Cell id={id} title={titleEl} token={token} />;
  return inline ? <Tag style={{ marginBottom: 4, marginTop: 4 }}>{children}</Tag> : children;
};

interface CellLinksProps {
  data: { id: string; title: string }[];
  onClick: (id: string) => void;
}

Cell.Links = ({ data, onClick }: CellLinksProps) => {
  const cellData = sortBy(clone(data), 'title');
  if (!cellData.length) return <>-</>;

  const visibleData = cellData.splice(0, 5);
  const remainingCount = cellData.length;
  return (
    <>
      {visibleData.map((d) => (
        <Tag key={d.id} style={{ marginBottom: 4, marginTop: 4 }}>
          <Button
            key={d.id}
            type="link"
            size="small"
            style={{ border: 'none' }}
            onClick={() => onClick(d.id)}
          >
            {d.title}
          </Button>
        </Tag>
      ))}

      {remainingCount ? (
        <Tooltip
          placement="rightTop"
          overlayStyle={{ zIndex: 900 }}
          title={cellData.map((d) => (
            <Button
              key={d.id}
              size="small"
              type="link"
              onClick={() => onClick(d.id)}
              style={{ display: 'block', color: '#fff' }}
            >
              {d.title}
            </Button>
          ))}
        >
          <Button size="small">+{remainingCount} More</Button>
        </Tooltip>
      ) : null}
    </>
  );
};

export default Cell;
